import type {ReactNode} from "react";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {MenuSectionConfig} from "@vesta/aergia/components/pageLayoutSwitchSites";
import type {
    ArgusClickConfig,
    HeaderItem,
    HeaderMenuLink,
    NavItem,
    NavNest,
    NavTarget,
    NestPageRef,
    ScaffoldApp,
    ScaffoldRouteContext,
    StatusNavStatus,
    ViewType,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceConfig";
import {
    doesScaffoldAppContainPageKey,
    resolveDefaultPageKeyForApp,
    resolveFirstPageKeyInNav,
} from "~/workspaceConfig/scaffoldNavigation";
import {getStatusNavIconColor} from "~/workspaceConfig/configStatusNavColors";
import {ModuleStatusBarsIcon} from "~/components/icons/moduleStatusBarsIcon";
import {resolveScaffoldIcon, resolveViewIcon, viewLabel} from "~/workspaceConfig/scaffold/scaffoldNavIcons";
import {
    buildConfigScaffoldHref,
    isPartiallyActiveNavTarget,
    isConfigScaffoldNavTarget,
    matchesNavTargetRoute,
    resolveExternalHref,
    resolvePageIdFromNavTarget,
    resolveSearchFromNavTarget,
} from "~/workspaceConfig/scaffold/matchScaffoldActiveState";
import {argusDomAttributes} from "~/workspaceConfig/argusDomAttributes";

export type ScaffoldNavResolvedNestedItem = {
    id: Uuid;
    label: string;
    href: string;
    elemBefore?: ReactNode;
    isActive: boolean;
    argus?: ArgusClickConfig | null;
};

export type ScaffoldNavResolvedItem =
    | {
          type: "divider";
          id: Uuid;
      }
    | {
          type: "sectionHeading";
          id: Uuid;
          label: string;
      }
    | {
          type: "linkItem";
          id: Uuid;
          label: string;
          href: string;
          icon: ReactNode;
          nestType?: "list" | "collapsibleList" | "accordion";
          nestedItems?: Array<ScaffoldNavResolvedNestedItem>;
          pinToMobileBar?: boolean;
          isDisabled?: boolean;
          isActive: boolean;
          isPartiallyActive: boolean;
          isExternal: boolean;
          argus?: ArgusClickConfig | null;
      };

function buildHrefForTarget(args: {
    workspaceId: Uuid;
    target: NavTarget;
    view?: ViewType | string;
    statusId?: Uuid;
}): string {
    const pageId = resolvePageIdFromNavTarget(args.target);
    if (pageId === null) {
        return "#";
    }
    const search = resolveSearchFromNavTarget({
        target: args.target,
        view: args.view,
        statusId: args.statusId,
    });
    return buildConfigScaffoldHref({
        workspaceId: args.workspaceId,
        pageId,
        view: search.view,
        statusId: search.statusId,
    });
}

function buildStatusNestedItems(args: {
    nest: Extract<NavNest, {kind: "status"}>;
    target: NavTarget;
    workspaceId: Uuid;
    route: ScaffoldRouteContext;
}): Array<ScaffoldNavResolvedNestedItem> {
    return args.nest.statuses.map((status: StatusNavStatus) => {
        return {
            id: status.id,
            label: status.label,
            href: buildHrefForTarget({
                workspaceId: args.workspaceId,
                target: args.target,
                statusId: status.id,
            }),
            elemBefore: (
                <ModuleStatusBarsIcon
                    color={getStatusNavIconColor(status.color)}
                    label={status.label}
                />
            ),
            isActive: matchesNavTargetRoute({
                target: args.target,
                route: args.route,
                statusId: status.id,
            }),
        };
    });
}

function buildViewNestedItems(args: {
    nest: Extract<NavNest, {kind: "views"}>;
    target: NavTarget;
    workspaceId: Uuid;
    route: ScaffoldRouteContext;
}): Array<ScaffoldNavResolvedNestedItem> {
    return args.nest.views.map((view: ViewType) => {
        return {
            id: `scaffold-view-${view}` as Uuid,
            label: viewLabel(view),
            href: buildHrefForTarget({
                workspaceId: args.workspaceId,
                target: args.target,
                view: view,
            }),
            elemBefore: resolveViewIcon(view),
            isActive: matchesNavTargetRoute({
                target: args.target,
                route: args.route,
                view: view,
            }),
        };
    });
}

function buildPageNestedItems(args: {
    nest: Extract<NavNest, {kind: "pages"}>;
    workspaceId: Uuid;
    route: ScaffoldRouteContext;
}): Array<ScaffoldNavResolvedNestedItem> {
    return args.nest.pages.map((page: NestPageRef) => ({
        id: page.pageId,
        label: page.label,
        href: buildConfigScaffoldHref({
            workspaceId: args.workspaceId,
            pageId: page.pageId,
        }),
        ...(page.icon === undefined || page.icon === null ? {} : {elemBefore: resolveScaffoldIcon(page.icon)}),
        isActive: args.route.pageKey === page.pageId,
        ...(page.argus === undefined ? {} : {argus: page.argus}),
    }));
}

function resolveNestForNavItem(args: {
    nest: NavNest;
    target: NavTarget;
    workspaceId: Uuid;
    route: ScaffoldRouteContext;
}): {
    nestType?: "list" | "collapsibleList" | "accordion";
    nestedItems?: Array<ScaffoldNavResolvedNestedItem>;
    parentSearchOverride?: Partial<Pick<ScaffoldRouteContext, "view" | "statusId">>;
    nestedStatusIds: Array<Uuid>;
    nestedViews: Array<string>;
    nestedPageIds: Array<Uuid>;
} {
    const {nest, target} = args;

    if (nest.kind === "tabs") {
        return {
            nestedStatusIds: [],
            nestedViews: [],
            nestedPageIds: [],
        };
    }

    if (nest.kind === "status") {
        const nestedItems = buildStatusNestedItems({
            nest,
            target,
            workspaceId: args.workspaceId,
            route: args.route,
        });
        return {
            nestType: nest.nestType,
            nestedItems,
            nestedStatusIds: nest.statuses.map((status: StatusNavStatus) => status.id),
            nestedViews: [],
            nestedPageIds: [],
        };
    }

    if (nest.kind === "views") {
        const nestedItems = buildViewNestedItems({
            nest,
            target,
            workspaceId: args.workspaceId,
            route: args.route,
        });
        return {
            nestType: nest.nestType,
            nestedItems,
            nestedStatusIds: [],
            nestedViews: nest.views,
            nestedPageIds: [],
        };
    }

    if (nest.kind === "pages") {
        const nestedItems = buildPageNestedItems({
            nest,
            workspaceId: args.workspaceId,
            route: args.route,
        });
        return {
            // Parent is its own page (Products, Machines). Accordion is toggle-only
            // in the sidebar, so page nests always use a navigable collapsible parent.
            nestType: "collapsibleList",
            nestedItems,
            nestedStatusIds: [],
            nestedViews: [],
            nestedPageIds: nest.pages.map((page: NestPageRef) => page.pageId),
        };
    }

    return {
        nestedStatusIds: [],
        nestedViews: [],
        nestedPageIds: [],
    };
}

function resolveNavItemHref(args: {
    target: NavTarget;
    workspaceId: Uuid;
    parentSearchOverride?: Partial<Pick<ScaffoldRouteContext, "view" | "statusId">>;
}): {href: string; isExternal: boolean} {
    const externalHref = resolveExternalHref({
        target: args.target,
        workspaceId: args.workspaceId,
    });
    if (externalHref !== null) {
        return {href: externalHref, isExternal: true};
    }
    if (isConfigScaffoldNavTarget(args.target) === false) {
        return {href: "#", isExternal: false};
    }
    const pageId = resolvePageIdFromNavTarget(args.target);
    if (pageId === null) {
        return {href: "#", isExternal: false};
    }
    const search = args.parentSearchOverride ?? resolveSearchFromNavTarget({target: args.target});
    return {
        href: buildConfigScaffoldHref({
            workspaceId: args.workspaceId,
            pageId,
            view: search.view,
            statusId: search.statusId,
        }),
        isExternal: false,
    };
}

function scaffoldNavItemToResolvedItem(args: {
    item: NavItem;
    workspaceId: Uuid;
    route: ScaffoldRouteContext;
    isRestrictedToProfile: boolean;
}): ScaffoldNavResolvedItem {
    const {item} = args;

    if (item.type === "divider") {
        return {
            type: "divider",
            id: item.id,
        };
    }

    if (item.type === "sectionHeading") {
        return {
            type: "sectionHeading",
            id: item.id,
            label: item.label,
        };
    }

    const nestResolved =
        item.nest === undefined || item.nest === null
            ? {
                  nestedStatusIds: [] as Array<Uuid>,
                  nestedViews: [] as Array<string>,
                  nestedPageIds: [] as Array<Uuid>,
              }
            : resolveNestForNavItem({
                  nest: item.nest,
                  target: item.target,
                  workspaceId: args.workspaceId,
                  route: args.route,
              });

    const {href, isExternal} = resolveNavItemHref({
        target: item.target,
        workspaceId: args.workspaceId,
        parentSearchOverride: nestResolved.parentSearchOverride,
    });

    const isActive = matchesNavTargetRoute({
        target: item.target,
        route: args.route,
    });
    const isPartiallyActive =
        isActive === false &&
        isPartiallyActiveNavTarget({
            target: item.target,
            route: args.route,
            nestedStatusIds: nestResolved.nestedStatusIds,
            nestedViews: nestResolved.nestedViews,
            nestedPageIds: nestResolved.nestedPageIds,
        });

    return {
        type: "linkItem",
        id: item.id,
        label: item.label,
        href,
        icon: resolveScaffoldIcon(item.icon),
        ...(nestResolved.nestType !== undefined ? {nestType: nestResolved.nestType} : {}),
        ...(nestResolved.nestedItems !== undefined && nestResolved.nestedItems.length > 0
            ? {nestedItems: nestResolved.nestedItems}
            : {}),
        ...(item.pinToMobileBar === true ? {pinToMobileBar: true} : {}),
        ...(args.isRestrictedToProfile === true ? {isDisabled: true} : {}),
        isActive: isActive || isPartiallyActive,
        isPartiallyActive,
        isExternal,
        ...(item.argus === undefined ? {} : {argus: item.argus}),
    };
}

export function resolveScaffoldNavigation(args: {
    nav: Array<NavItem> | null | undefined;
    workspaceId: Uuid;
    route: ScaffoldRouteContext;
    isRestrictedToProfile: boolean;
}): Array<ScaffoldNavResolvedItem> {
    return (args.nav ?? []).map((item) =>
        scaffoldNavItemToResolvedItem({
            item,
            workspaceId: args.workspaceId,
            route: args.route,
            isRestrictedToProfile: args.isRestrictedToProfile,
        }),
    );
}

export function resolveScaffoldApps(args: {
    apps: Array<ScaffoldApp>;
    pageKey: string;
}): Array<{id: Uuid; label: string; icon: ReactNode; isActive: boolean}> {
    return args.apps.map((app) => ({
        id: app.id,
        label: app.label,
        icon: resolveScaffoldIcon(app.icon),
        isActive: doesScaffoldAppContainPageKey(app, args.pageKey),
    }));
}

export {resolveDefaultPageKeyForApp as resolveDefaultPageKey, resolveFirstPageKeyInNav};

function resolveHeaderMenuLink(args: {
    link: HeaderMenuLink;
    workspaceId: Uuid;
    route: ScaffoldRouteContext;
}): MenuSectionConfig["items"][number] {
    const externalHref = resolveExternalHref({
        target: args.link.target,
        workspaceId: args.workspaceId,
    });
    const pageId = resolvePageIdFromNavTarget(args.link.target);
    const search = resolveSearchFromNavTarget({target: args.link.target});
    const href =
        externalHref ??
        (pageId !== null
            ? buildConfigScaffoldHref({
                  workspaceId: args.workspaceId,
                  pageId,
                  view: search.view,
                  statusId: search.statusId,
              })
            : `/w/${args.workspaceId}/${args.route.pageKey}`);
    const icon = args.link.icon;
    return {
        type: "link",
        id: args.link.id,
        href,
        title: args.link.label,
        description: args.link.description ?? undefined,
        paddingInline: "px-ds-space-100",
        className: "rounded-[3px]",
        ...(icon === undefined || icon === null
            ? {}
            : {
                  iconBefore: () => resolveScaffoldIcon(icon),
              }),
        ...argusDomAttributes(args.link.argus),
    };
}

export function resolveScaffoldHeaderMenuSections(args: {
    header: Array<HeaderItem>;
    kind: "settings" | "teamSpace";
    workspaceId: Uuid;
    route: ScaffoldRouteContext;
}): Array<MenuSectionConfig> {
    const sections: Array<MenuSectionConfig> = [];
    for (const item of args.header) {
        if (item.type !== args.kind) {
            continue;
        }
        sections.push({
            id: item.id,
            title: item.title,
            items: item.items.map((link: HeaderMenuLink) =>
                resolveHeaderMenuLink({
                    link,
                    workspaceId: args.workspaceId,
                    route: args.route,
                }),
            ),
        });
    }
    return sections;
}

export function resolveScaffoldHeaderChromeArgus(args: {
    header: Array<HeaderItem>;
    kind: "create" | "notifications" | "tickets";
}): ArgusClickConfig | null {
    const item = args.header.find((headerItem) => headerItem.type === args.kind);
    if (item === undefined) {
        return null;
    }
    if (item.type !== "create" && item.type !== "notifications" && item.type !== "tickets") {
        return null;
    }
    if (item.argus === undefined || item.argus === null) {
        return null;
    }
    return item.argus;
}
